import React, { useEffect, useState } from 'react';

// ==========================================
// PROCESSING ANIMATION COMPONENT
// Drop this into your pipeline-card or eka-card
// when isGeneratingEka === true
// ==========================================

type StepStatus = 'done' | 'active' | 'pending';
const STEPS: ReadonlyArray<{ label: string; status: StepStatus }> = [
  { label: 'Audio transcription',       status: 'done'    },
  { label: 'Clinical NLP extraction',   status: 'done'    },
  // { label: 'Structuring EMR sections',  status: 'active'  },
  // { label: 'Triage assessment',         status: 'pending' },
  { label: 'Generating summary notes',  status: 'pending' },
];

const HEADLINE_CYCLE = [
  ['Transcribing audio',          'Detecting speech patterns and converting to clinical text…'],
  ['Extracting clinical context', 'Identifying diagnoses, symptoms, and medical entities…'],
  ['Finalising clinical notes',   'Compiling your complete AI-generated documentation…'],
] as const;

const BAR_COUNT = 44;

// Pre-compute random bar heights / durations once
const BAR_DATA = Array.from({ length: BAR_COUNT }, (_, i) => ({
  height: 8 + Math.sin(i * 0.45) * 8 + (((i * 13) % 9)),   // deterministic pseudo-random
  duration: 0.9 + ((i * 7) % 6) / 10,
  delay: -(i / BAR_COUNT) * 1.2,
  opacity: 0.5 + (i % 3) * 0.15,
}));

// ── Animated counter hook ──────────────────────────────────────────────────
function useCountUp(target: number, durationMs: number, startAfterMs = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let raf: number;
    timer = setTimeout(() => {
      const start = performance.now();
      const tick = (now: number) => {
        const t = Math.min((now - start) / durationMs, 1);
        setValue(Math.round(t * target));
        if (t < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, startAfterMs);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [target, durationMs, startAfterMs]);
  return value;
}

// ── Visible-steps reveal (staggered) ──────────────────────────────────────
function useStaggeredSteps(count: number, intervalMs = 450) {
  const [visible, setVisible] = useState(0);
  useEffect(() => {
    setVisible(0);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setVisible(i);
      if (i >= count) clearInterval(id);
    }, intervalMs);
    return () => clearInterval(id);
  }, [count, intervalMs]);
  return visible;
}

// ── Headline cycler ────────────────────────────────────────────────────────
function useHeadlineCycle(items: typeof HEADLINE_CYCLE, periodMs = 2800) {
  const [idx, setIdx]       = useState(0);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    let fadeTimer: ReturnType<typeof setTimeout> | null = null;
    const id = setInterval(() => {
      setFading(true);
      fadeTimer = setTimeout(() => {
        setIdx(p => (p + 1) % items.length);
        setFading(false);
      }, 320);
    }, periodMs);
    return () => {
      clearInterval(id);
      if (fadeTimer) clearTimeout(fadeTimer);
    };
  }, [items, periodMs]);
  return { headline: items[idx][0], subline: items[idx][1], fading };
}

// ── Confidence counter (grows slowly to 94%) ──────────────────────────────
function useConfidence() {
  const [conf, setConf] = useState<number | null>(null);
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const timer = setTimeout(() => {
      let p = 72;
      intervalId = setInterval(() => {
        p += 2;
        setConf(p);
        if (p >= 94 && intervalId) clearInterval(intervalId);
      }, 120);
    }, 2000);
    return () => {
      clearTimeout(timer);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);
  return conf;
}

// ── CSS-in-JS styles (no external stylesheet needed) ─────────────────────
const S: Record<string, React.CSSProperties> = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '2rem 1.5rem',
    gap: 0,
  },
  stageLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#888780',            // gray-400 from your palette
    marginBottom: '1.5rem',
  },
  waveContainer: {
    width: '100%',
    maxWidth: 380,
    height: 64,
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    justifyContent: 'center',
    marginBottom: '1.75rem',
  },
  steps: {
    width: '100%',
    maxWidth: 360,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginBottom: '1.75rem',
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    borderRadius: 8,
    border: '0.5px solid #D3D1C7',   // gray-100
    background: '#F1EFE8',            // gray-50
  },
  metrics: {
    display: 'flex',
    gap: 10,
    width: '100%',
    maxWidth: 360,
    marginBottom: '1.5rem',
  },
  metric: {
    flex: 1,
    background: '#F1EFE8',
    border: '0.5px solid #D3D1C7',
    borderRadius: 8,
    padding: '10px 12px',
    textAlign: 'center' as const,
  },
  metricValue: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1e3a5f',
    display: 'block',
    marginBottom: 2,
    fontVariantNumeric: 'tabular-nums',
  },
  metricLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#888780',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.07em',
  },
  headline: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1e3a5f',
    margin: '0 0 4px',
    textAlign: 'center' as const,
    transition: 'opacity 0.3s ease',
  },
  subline: {
    fontSize: 13,
    color: '#444441',
    textAlign: 'center' as const,
    lineHeight: 1.6,
    maxWidth: 340,
    margin: 0,
    transition: 'opacity 0.3s ease',
  },
};

// ── Animated Pranaa logo ───────────────────────────────────────────────────
const PARTICLES: Array<{
  x: string; y: string; w: number; c: string; dur: string; delay: string;
  tx: string; ty: string; tx2: string; ty2: string; tx3: string; ty3: string;
}> = [
  { x: '27%', y: '20%', w: 6, c: '#e56c98', dur: '3.1s', delay: '0s',
    tx: '-16px', ty: '-20px', tx2: '-24px', ty2: '-8px', tx3: '-10px', ty3: '-28px' },
  { x: '70%', y: '18%', w: 5, c: '#97b88f', dur: '3.5s', delay: '0.7s',
    tx: '18px', ty: '-18px', tx2: '26px', ty2: '-6px', tx3: '12px', ty3: '-26px' },
  { x: '18%', y: '46%', w: 5, c: '#eb96b2', dur: '2.7s', delay: '1.3s',
    tx: '-20px', ty: '-12px', tx2: '-12px', ty2: '-24px', tx3: '-28px', ty3: '-4px' },
  { x: '79%', y: '42%', w: 4, c: '#e56c98', dur: '3.8s', delay: '2.0s',
    tx: '16px', ty: '-18px', tx2: '8px', ty2: '-28px', tx3: '22px', ty3: '-10px' },
  { x: '44%', y: '8%',  w: 4, c: '#d18899', dur: '2.9s', delay: '0.4s',
    tx: '-6px', ty: '-22px', tx2: '8px', ty2: '-14px', tx3: '-2px', ty3: '-30px' },
  { x: '60%', y: '66%', w: 3, c: '#81a969', dur: '3.3s', delay: '1.9s',
    tx: '12px', ty: '-14px', tx2: '20px', ty2: '-4px', tx3: '6px', ty3: '-22px' },
  { x: '34%', y: '62%', w: 3, c: '#97b88f', dur: '2.6s', delay: '2.4s',
    tx: '-14px', ty: '-10px', tx2: '-6px', ty2: '-22px', tx3: '-20px', ty3: '-2px' },
];

const PranaaLogo: React.FC = () => (
  <div className="pl-stage">
    <div className="pl-glow pl-glow-outer" />
    <div className="pl-glow pl-glow-inner" />
    <div className="pl-particles">
      {PARTICLES.map((p, i) => (
        <span
          key={i}
          className="pl-particle"
          style={{
            left: p.x, top: p.y, width: p.w, height: p.w, background: p.c,
            animationDuration: p.dur, animationDelay: p.delay,
            ['--tx' as any]: p.tx, ['--ty' as any]: p.ty,
            ['--tx2' as any]: p.tx2, ['--ty2' as any]: p.ty2,
            ['--tx3' as any]: p.tx3, ['--ty3' as any]: p.ty3,
          } as React.CSSProperties}
        />
      ))}
    </div>
    <div className="pl-ground-shadow" />
    <div className="pl-logo-wrap">
      <svg width="120" height="151" viewBox="0 0 81 102" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#pl-clip)">
          <path className="pl-wing-left" fillRule="evenodd" clipRule="evenodd" d="M40.5051 56.4803C40.5051 56.4803 40.3632 56.7641 40.049 57.2236C38.6673 59.2643 33.9716 64.768 23.8098 64.1903C11.3576 63.4841 5.97949 55.6289 5.97949 55.6289C8.37466 53.1422 10.7969 51.3887 13.1853 50.213C27.3941 43.2058 40.5084 56.4803 40.5084 56.4803H40.5051Z" fill="#97B88F"/>
          <path className="pl-wing-right" fillRule="evenodd" clipRule="evenodd" d="M75.0338 55.6289C75.0338 55.6289 69.6591 63.4841 57.2035 64.1903C47.0384 64.768 42.3426 59.2676 40.9609 57.2236C40.6468 56.7641 40.5049 56.4803 40.5049 56.4803C40.5049 56.4803 53.6226 43.2058 67.8281 50.213C70.2165 51.3887 72.6387 53.1422 75.0338 55.6289Z" fill="#97B88F"/>
          <path className="pl-flame-left" fillRule="evenodd" clipRule="evenodd" d="M40.6229 56.2166H40.6128C40.5723 56.2166 40.5317 56.22 40.4946 56.2234C40.4135 56.2268 40.329 56.2335 40.248 56.2335C40.2243 56.2335 40.2007 56.2369 40.177 56.2369C28.1269 56.8113 19.7049 54.1253 13.8335 49.9054C13.7761 49.8649 13.7221 49.8243 13.6646 49.7838C13.4687 49.6419 13.2727 49.4966 13.0836 49.3513C-1.80094 37.9046 0.651659 16.3762 0.651659 16.3762C34.7448 14.8828 39.7649 44.2834 40.5013 53.4969C40.5689 54.3483 40.5993 55.0308 40.6162 55.497V55.5274C40.6297 55.9734 40.6263 56.2133 40.6263 56.2133L40.6229 56.2166Z" fill="#EB96B2"/>
          <path className="pl-flame-right" fillRule="evenodd" clipRule="evenodd" d="M68.0105 49.2737C67.8213 49.4223 67.6254 49.5676 67.4294 49.7129C67.345 49.7771 67.2571 49.8379 67.1693 49.9021C61.3385 54.0915 52.9942 56.7708 41.0792 56.2505C40.9745 56.2471 40.8663 56.2403 40.7616 56.237C40.7177 56.237 40.6738 56.2336 40.6299 56.2302C40.6299 56.2302 40.6287 56.2302 40.6265 56.2302C40.586 56.2302 40.542 56.2302 40.5015 56.2234C40.461 56.2234 40.4204 56.2201 40.3799 56.2167C40.3799 56.2167 40.3799 56.1356 40.3799 55.9869C40.3833 55.595 40.4069 54.7267 40.5049 53.5003C41.2413 44.2868 46.258 14.8862 80.3512 16.3796C80.3512 16.3796 82.7903 37.8134 68.0139 49.2737H68.0105Z" fill="#EB96B2"/>
          <path className="pl-accent-r" fillRule="evenodd" clipRule="evenodd" d="M67.1694 49.9023C61.3386 54.0917 52.9943 56.771 41.0793 56.2507C40.9745 56.2473 40.8664 56.2405 40.7617 56.2371C42.613 54.4904 54.3726 44.135 67.1728 49.9023H67.1694Z" fill="#D18899"/>
          <path className="pl-accent-l" fillRule="evenodd" clipRule="evenodd" d="M40.2513 56.2336C40.2277 56.2336 40.204 56.237 40.1804 56.237C28.1302 56.8114 19.7083 54.1254 13.8369 49.9055C26.6337 44.1315 38.3899 54.4801 40.2513 56.2336Z" fill="#D18899"/>
          <path className="pl-center-drop" fillRule="evenodd" clipRule="evenodd" d="M40.4435 0C40.4435 0 72.3814 26.9038 40.5044 56.4801C40.5044 56.4801 8.68137 30.9919 40.4435 0Z" fill="#E56C98"/>
        </g>
        <defs>
          <clipPath id="pl-clip">
            <rect width="80" height="102" fill="white" transform="translate(0.5)"/>
          </clipPath>
        </defs>
      </svg>
    </div>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────
export const ProcessingAnimation: React.FC = () => {
  const words      = useCountUp(312, 1600, 400);
  const sections   = useCountUp(7,   900,  1200);
  const confidence = useConfidence();
  const visSteps   = useStaggeredSteps(STEPS.length, 450);
  const { headline, subline, fading } = useHeadlineCycle(HEADLINE_CYCLE);

  return (
    <div style={S.wrapper} aria-live="polite" aria-label="Generating clinical notes">

      <p style={S.stageLabel}>AI analysis in progress</p>

      {/* Animated Pranaa logo */}
      <PranaaLogo />

       <p style={{ ...S.headline, opacity: fading ? 0 : 1 }}>{headline}</p>
      <p style={{ ...S.subline,  opacity: fading ? 0 : 1 }}>{subline}</p>


      {/* Waveform */}
      <div style={S.waveContainer}>
        {BAR_DATA.map((b, i) => (
          <div
            key={i}
            style={{
              width: 4,
              height: b.height + 20,
              borderRadius: 2,
              background: '#e56c98',
              opacity: b.opacity,
              flexShrink: 0,
              animation: `msWaveBar ${b.duration}s ease-in-out ${b.delay}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Steps */}
      <div style={S.steps}>
        {STEPS.slice(0, visSteps).map((step, i) => (
          <div
            key={i}
            style={{
              ...S.step,
              animation: 'msFadeUp 0.45s ease both',
            }}
          >
            <div style={{
              width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
              background: step.status === 'done'
                ? '#1D9E75'
                : step.status === 'active'
                  ? '#185FA5'
                  : '#B4B2A9',
              animation: step.status === 'active'
                ? 'msDotPulse 1s ease-in-out infinite'
                : undefined,
            }}/>
            <span style={{
              ...{fontSize: 13, fontWeight: 400, flex: 1},
              color: step.status === 'pending' ? '#888780' : '#1e3a5f',
            }}>
              {step.label}
            </span>
            {step.status !== 'pending' && (
              <span style={{
                fontSize: 10, fontWeight: 700,
                padding: '2px 8px', borderRadius: 20,
                background: step.status === 'done' ? '#E1F5EE' : '#E6F1FB',
                color:      step.status === 'done' ? '#0F6E56' : '#0C447C',
              }}>
                {step.status === 'done' ? 'done' : 'processing'}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Metrics */}
      {/* <div style={S.metrics}>
        {[
          { value: words.toString(),                       label: 'words'      },
          { value: sections.toString(),                    label: 'sections'   },
          { value: confidence != null ? `${confidence}%` : '—', label: 'confidence' },
        ].map(m => (
          <div key={m.label} style={S.metric}>
            <span style={S.metricValue}>{m.value}</span>
            <span style={S.metricLabel}>{m.label}</span>
          </div>
        ))}
      </div> */}

      {/* Cycling headline */}
      {/* <p style={{ ...S.headline, opacity: fading ? 0 : 1 }}>{headline}</p>
      <p style={{ ...S.subline,  opacity: fading ? 0 : 1 }}>{subline}</p> */}

      {/* Keyframes injected once */}
      <style>{`
        @keyframes msWaveBar {
          0%, 100% { transform: scaleY(0.15); opacity: 0.35; }
          50%       { transform: scaleY(1);    opacity: 1;    }
        }
        @keyframes msFadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
        @keyframes msDotPulse {
          0%, 100% { opacity: 1;   transform: scale(1);    }
          50%      { opacity: 0.4; transform: scale(0.75); }
        }

        /* ── Pranaa logo ── */
        .pl-stage {
          position: relative;
          width: 100%;
          min-height: 200px;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          padding: 24px 0 36px;
          margin-bottom: 1.5rem;
          overflow: visible;
          box-sizing: border-box;
        }
        .pl-glow {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          animation: plGlowPulse 4s ease-in-out infinite;
        }
        .pl-glow-outer {
          width: 200px; height: 200px;
          background: radial-gradient(circle, rgba(229,108,152,0.13) 0%, rgba(235,150,178,0.06) 55%, transparent 80%);
          bottom: 36px; left: 50%;
          transform: translate(-50%, 25%);
        }
        .pl-glow-inner {
          width: 110px; height: 110px;
          background: radial-gradient(circle, rgba(151,184,143,0.18) 0%, transparent 70%);
          bottom: 36px; left: 50%;
          transform: translate(-50%, 18%);
          animation-delay: 1.2s;
        }
        @keyframes plGlowPulse {
          0%, 100% { opacity: 0.7; }
          50%      { opacity: 1;   }
        }
        .pl-particles {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 3;
        }
        .pl-particle {
          position: absolute;
          border-radius: 50%;
          opacity: 0;
          animation-name: plParticleDrift;
          animation-iteration-count: infinite;
          animation-timing-function: ease-in-out;
        }
        @keyframes plParticleDrift {
          0%   { opacity: 0;   transform: translate(0,0) scale(0.5); }
          25%  { opacity: 0.9; transform: translate(var(--tx), var(--ty)) scale(1); }
          75%  { opacity: 0.4; transform: translate(var(--tx2), var(--ty2)) scale(0.7); }
          100% { opacity: 0;   transform: translate(var(--tx3), var(--ty3)) scale(0.3); }
        }
        .pl-ground-shadow {
          position: absolute;
          bottom: 18px; left: 50%;
          transform: translateX(-50%);
          width: 60px; height: 8px;
          background: rgba(229,108,152,0.18);
          border-radius: 50%;
          filter: blur(5px);
          z-index: 1;
          animation: plShadowSync 4s ease-in-out infinite;
        }
        @keyframes plShadowSync {
          0%, 100% { transform: translateX(-50%) scaleX(1);    opacity: 0.7; }
          40%      { transform: translateX(-50%) scaleX(0.68); opacity: 0.3; }
          75%      { transform: translateX(-50%) scaleX(0.84); opacity: 0.5; }
        }
        .pl-logo-wrap {
          position: relative;
          z-index: 2;
          filter: drop-shadow(0 14px 28px rgba(229,108,152,0.28))
                  drop-shadow(0 4px 8px rgba(0,0,0,0.07));
          animation: plLogoFloat 4s ease-in-out infinite;
        }
        @keyframes plLogoFloat {
          0%, 100% { transform: translateY(0px) rotate(-0.4deg); }
          40%      { transform: translateY(-10px) rotate(0.5deg); }
          75%      { transform: translateY(-5px) rotate(-0.2deg); }
        }
        .pl-logo-wrap svg .pl-flame-left  { animation: plFlameLeft  3s ease-in-out infinite; transform-origin: 40px 56px; }
        .pl-logo-wrap svg .pl-flame-right { animation: plFlameRight 3s ease-in-out infinite; transform-origin: 41px 56px; }
        .pl-logo-wrap svg .pl-wing-right  { animation: plWingRight  4s ease-in-out infinite; transform-origin: 40px 56px; }
        .pl-logo-wrap svg .pl-wing-left   { animation: plWingLeft   4s ease-in-out infinite; transform-origin: 41px 56px; }
        .pl-logo-wrap svg .pl-accent-r    { animation: plAccentPop  3s ease-in-out infinite; transform-origin: 54px 53px; }
        .pl-logo-wrap svg .pl-accent-l    { animation: plAccentPop  3s ease-in-out infinite 0.3s; transform-origin: 27px 53px; }
        .pl-logo-wrap svg .pl-center-drop { animation: plCenterBob  4s ease-in-out infinite; transform-origin: 40px 56px; }
        @keyframes plFlameLeft {
          0%, 100% { transform: scaleX(1)    scaleY(1)    skewX(0deg); }
          40%      { transform: scaleX(1.025) scaleY(1.02) skewX(-1.2deg); }
          70%      { transform: scaleX(0.98) scaleY(0.99) skewX(0.6deg); }
        }
        @keyframes plFlameRight {
          0%, 100% { transform: scaleX(1)    scaleY(1)    skewX(0deg); }
          40%      { transform: scaleX(1.025) scaleY(1.02) skewX(1.2deg); }
          70%      { transform: scaleX(0.98) scaleY(0.99) skewX(-0.6deg); }
        }
        @keyframes plWingRight {
          0%, 100% { transform: rotate(0deg) scaleX(1); }
          35%      { transform: rotate(2.5deg) scaleX(1.03); }
          70%      { transform: rotate(1deg) scaleX(1.01); }
        }
        @keyframes plWingLeft {
          0%, 100% { transform: rotate(0deg) scaleX(1); }
          35%      { transform: rotate(-2.5deg) scaleX(1.03); }
          70%      { transform: rotate(-1deg) scaleX(1.01); }
        }
        @keyframes plAccentPop {
          0%, 100% { opacity: 1;    transform: scale(1);    }
          50%      { opacity: 0.75; transform: scale(0.93); }
        }
        @keyframes plCenterBob {
          0%, 100% { transform: scaleY(1)     scaleX(1);     }
          40%      { transform: scaleY(1.018) scaleX(0.993); }
          75%      { transform: scaleY(1.008) scaleX(0.997); }
        }
      `}</style>
    </div>
  );
};

export default ProcessingAnimation;